/**
 * 배포 뒤 옛 화면 조각(JS 청크)을 못 불러올 때 한 번 새로고침해 새 버전으로 바꾼다(2026-09-27).
 *
 * 앱(원격 URL WebView)은 백그라운드에 몇 시간씩 살아 있어, 그사이 배포가 두 번 나면 들고 있던 화면의
 * 조각 파일이 지워져(static_deploy 가 두 번 전 빌드 assets 삭제) 다음 화면 이동에서 불러오기가 실패하고
 * 흰 화면이 됐다. 새로고침하면 최신 index.html 을 받아 정상으로 돌아온다.
 * 같은 오류가 계속되면 무한 새로고침을 막기 위해 30초 안에는 다시 새로고침하지 않는다.
 */
const RELOAD_KEY = 'mc_chunk_reload_at';
const RELOAD_GUARD_MS = 30 * 1000;

/** 실패해도 되는 조각을 불러오는 중인 개수 — 0 보다 크면 vite:preloadError 로 새로고침하지 않는다 */
let optionalLoads = 0;

/**
 * 없어도 되는 조각(예: 사파리용 WebP 인코더)을 불러온다 — 실패하면 새로고침하지 않고 예외를 그대로 던진다.
 * 업로드 중에 새로고침되면 쓰던 글이 날아가므로, 호출하는 쪽이 예외를 받아 대체 동작으로 넘어간다.
 * 그사이 다른 화면 조각이 실패해도 그 오류는 [AppErrorBoundary] 가 받아 새로고침한다.
 */
export const loadOptionalChunk = async <T>(load: () => Promise<T>): Promise<T> => {
    optionalLoads++;
    try {
        return await load();
    } finally {
        optionalLoads--;
    }
};

/** 청크(동적 import) 불러오기 실패인지 — 브라우저마다 문구가 다르다 */
export const isChunkLoadError = (error: unknown): boolean => {
    const message: string = String((error as { message?: string })?.message ?? error ?? '');
    return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError|Loading chunk .* failed/i.test(message);
};

/** 최근에 새로고침하지 않았으면 새로고침한다. 새로고침했으면 true */
export const reloadOnce = (): boolean => {
    try {
        const last: number = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
        if (Date.now() - last < RELOAD_GUARD_MS) return false;
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
        // 저장소가 막힌 환경 — 한 번은 새로고침(다음부터는 오류 화면이 받는다)
        if ((window as { __mcChunkReloaded?: boolean }).__mcChunkReloaded) return false;
        (window as { __mcChunkReloaded?: boolean }).__mcChunkReloaded = true;
    }
    window.location.reload();
    return true;
};

/** Vite 가 동적 import 실패를 알릴 때 새로고침 — main.tsx 에서 한 번 등록 */
export const installChunkReload = (): void => {
    window.addEventListener('vite:preloadError', (event: Event) => {
        if (optionalLoads > 0) return;
        if (reloadOnce()) event.preventDefault();
    });
};
